/**
 * Pairs the /portfolio drawers with the "Owed action today" rows above them.
 *
 * THE INVARIANT: nothing actionable may live only inside a collapsed drawer.
 *
 * Stage 3 of the Portfolio rework hides roughly 90% of the page behind collapsed
 * sections. That is only safe if the triage panel at the top is a complete index of
 * what needs the reader — otherwise collapsing does not fix the page, it just moves
 * the problem one click further out of sight, which is worse than the scroll it
 * replaced.
 *
 * So the badges are DERIVED from the owed-action rows rather than computed a second
 * time. A drawer cannot acquire a badge without a matching row, because it has no
 * independent source for one. `findInvariantViolations` then checks the other
 * direction — that nothing owed and handled on this page lacks a drawer to open.
 *
 * Pure and React-free so it can be tested directly: see drawerInvariant.test.ts.
 */

export type SectionTone = "positive" | "warning" | "elevated" | "danger" | "info" | "neutral";

/** The subset of an OwedActionRow this module needs. */
export type OwedLike = {
  id: string;
  label: string;
  verdict: string;
  tone: SectionTone;
  needsAction: boolean;
  /** Page name, or "below" when it is handled in a drawer on this page. */
  handledAt: string;
};

/** A page section, declared once. `owedRowId` is the only source of a badge. */
export type SectionDef = {
  id: string;
  label: string;
  /** The owed-action row this section resolves, if any. */
  owedRowId?: string;
  defaultOpen?: boolean;
};

export type DrawerBadge = { text: string; tone: SectionTone };

export type BuiltSpec = {
  id: string;
  label: string;
  badge: DrawerBadge | null;
  defaultOpen?: boolean;
};

/** Handled-on-this-page marker used by owed rows. */
export const HANDLED_BELOW = "below";

/**
 * Build drawer specs whose badges come only from the owed rows, so a badge can never
 * exist without one.
 */
export function buildDrawerSpecs(sections: SectionDef[], owed: OwedLike[]): BuiltSpec[] {
  const byId = new Map(owed.map((r) => [r.id, r]));
  return sections.map((s) => {
    const row = s.owedRowId ? byId.get(s.owedRowId) : undefined;
    return {
      id: s.id,
      label: s.label,
      defaultOpen: s.defaultOpen,
      badge: row && row.needsAction ? { text: row.verdict, tone: row.tone } : null,
    };
  });
}

/**
 * Returns a human-readable violation for each way the invariant can break. Empty means
 * the page is safe to collapse.
 */
export function findInvariantViolations(sections: SectionDef[], owed: OwedLike[]): string[] {
  const problems: string[] = [];
  const owedById = new Map(owed.map((r) => [r.id, r]));

  // 1. A section may not reference an owed row that does not exist — that is how a
  //    badge silently stops rendering after someone renames a row.
  for (const s of sections) {
    if (s.owedRowId && !owedById.has(s.owedRowId)) {
      problems.push(`Section "${s.id}" references owed row "${s.owedRowId}", which does not exist.`);
    }
  }

  // 2. Anything owed AND handled on this page must have a drawer to open. Without
  //    this, an item could be flagged at the top with nowhere to go.
  const claimed = new Set(sections.map((s) => s.owedRowId).filter(Boolean) as string[]);
  for (const row of owed) {
    if (row.needsAction && row.handledAt === HANDLED_BELOW && !claimed.has(row.id)) {
      problems.push(
        `Owed row "${row.id}" (${row.label}) is handled on this page but no section claims it, ` +
          `so it would be flagged with nothing to open.`
      );
    }
  }

  // 3. Two sections may not claim the same row, or the reader gets the same badge twice
  //    and cannot tell which drawer resolves it.
  const seen = new Set<string>();
  for (const s of sections) {
    if (!s.owedRowId) continue;
    if (seen.has(s.owedRowId)) {
      problems.push(`Owed row "${s.owedRowId}" is claimed by more than one section.`);
    }
    seen.add(s.owedRowId);
  }

  return problems;
}

/**
 * The /portfolio sections, declared ONCE and imported by both the page and the test.
 *
 * It used to live inside PortfolioView.tsx as a module-private const, so the test
 * re-declared its own copy and asserted against that — which meant the "invariant" could
 * not fail when the page drifted. Three places claimed it was "enforced by a test, not by
 * convention"; a hand-copied literal IS a convention. Keeping the single object here is
 * what makes that claim true.
 *
 * `owedRowId` is the only source of a drawer badge: buildDrawerSpecs derives it from the
 * matching "Owed action today" row, so a drawer has no way to invent one.
 */
export const PORTFOLIO_SECTIONS: SectionDef[] = [
  { id: "positions", label: "Positions", owedRowId: "exits" },
  { id: "runway", label: "Runway" },
  { id: "rebalance", label: "Sizing & rebalance" },
  { id: "book-quality", label: "Book quality" },
  { id: "tickets", label: "Staged tickets", owedRowId: "staged" },
];

/**
 * Dev-only backstop. The test proves the wiring is sound for the cases it models; this
 * catches drift in the browser the moment it happens, against the REAL owed rows the page
 * built this render. Silent in production.
 */
export function assertDrawerInvariant(owed: OwedLike[]): void {
  if (process.env.NODE_ENV === "production") return;
  const problems = findInvariantViolations(PORTFOLIO_SECTIONS, owed);
  if (problems.length) {
    // eslint-disable-next-line no-console
    console.error(["[portfolio] drawer invariant violated:", ...problems].join("\n - "));
  }
}
